import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Divider, Grid, Typography,
  Stepper, Step, StepLabel, TextField, FormControlLabel, Checkbox
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from '../../../support/LoadingSpinner';
import axios from '../../../../services/ApiService';
import toast from 'react-hot-toast';
import { WorkflowTracker, ActionType, WorkflowComment } from '../../../types/ProgramTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import { paths } from '../../../../utils/paths';

const Workflows = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [workflows, setWorkflows] = useState<WorkflowTracker[]>([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState<WorkflowTracker[]>([]);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const { user } = useAuthContext();
  const location = useLocation();
  const userId = (location.state as { userId?: number })?.userId;
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkflows();
  }, []);

  useEffect(() => {
    filterWorkflows(searchTerm, showMyTasks);
  }, [workflows, searchTerm, showMyTasks]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      let response;
      
      if (userId) {
        response = await axios.post('/getWorkflowTrackersByUser', userId);
      } else {
        response = await axios.get('/getWorkflowTrackers');
      }
      
      const workflowData = Array.isArray(response.data) ? response.data : [];
      setWorkflows(workflowData);
      setFilteredWorkflows(workflowData);
    } catch (error) {
      console.error('Error fetching workflows.');
      setWorkflows([]);
      setFilteredWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    filterWorkflows(value, showMyTasks);
  };

  const handleMyTasksChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setShowMyTasks(checked);
    filterWorkflows(searchTerm, checked);
  };

  const filterWorkflows = (search: string, myTasks: boolean) => {
    if (!Array.isArray(workflows)) {
      setFilteredWorkflows([]);
      return;
    }

    let filtered = [...workflows];

    if (search) {
      filtered = filtered.filter(workflow => 
        workflow.programName.toLowerCase().includes(search) ||
        workflow.workflowName.toLowerCase().includes(search) ||
        workflow.userName.toLowerCase().includes(search)
      );
    }

    if (myTasks && user?.role) {
      const userType = user.role;
      filtered = filtered.filter(workflow => {
        const currentStep = workflow.workflowSteps.find(step => 
          workflow.workflowStepTrackers.some(t => 
            t.stepId === step.id && t.status === 'IN_PROGRESS'
          )
        );
        return currentStep?.stepActionOn === userType;
      });
    }

    setFilteredWorkflows(filtered);
  };

  const getCurrentStageIndex = (workflow: WorkflowTracker) => {
    if (workflow.status === 'COMPLETED') {
      return workflow.workflowSteps.length; // Return total steps to show all as completed
    }
    const completedCount = workflow.workflowStepTrackers.filter(t => t.status === 'COMPLETED').length;
    return workflow.workflowStepTrackers.some(t => t.status === 'IN_PROGRESS') ? completedCount : -1;
  };

  const getCurrentTask = (workflow: WorkflowTracker) => {
    const currentTracker = workflow.workflowStepTrackers.find(t => t.status === 'IN_PROGRESS');
    if (!currentTracker) return null;
    
    return workflow.workflowSteps.find(step => step.id === currentTracker.stepId);
  };

  const handleViewDetails = (assignmentId: number) => {
    let userType = user?.role.toLowerCase();
    if(userType === 'company'){
      userType = 'agent';
    }
    navigate('/' + userType + paths.common.workflowView, { 
      state: { assignmentId: assignmentId } 
    });
  };

  if (loading) return <LoadingSpinner loading={loading} />;
  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>BVT - Workflows</title>
      </Helmet>
      <Box sx={{ padding: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2 
        }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search workflows..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showMyTasks}
                onChange={handleMyTasksChange}
                color="primary"
              />
            }
            label="My Tasks"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Box>

        <Grid container spacing={2}>
          {filteredWorkflows.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No workflows found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm || showMyTasks 
                      ? 'No workflows match your current filters. Try adjusting your search criteria or filters.'
                      : 'There are no workflows available at the moment.'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            (Array.isArray(filteredWorkflows) ? [...filteredWorkflows] : [])
              .sort((a, b) => {
                // First sort by status (IN_PROGRESS first)
                const aInProgress = a.workflowStepTrackers.some(t => t.status === 'IN_PROGRESS');
                const bInProgress = b.workflowStepTrackers.some(t => t.status === 'IN_PROGRESS');
                
                if (aInProgress && !bInProgress) return -1;
                if (!aInProgress && bInProgress) return 1;
                
                // Then sort by updatedAt date (most recent first)
                const aDate = new Date(a.updatedAt).getTime();
                const bDate = new Date(b.updatedAt).getTime();
                return bDate - aDate;
              })
              .map((workflow) => {
              const currentTask = getCurrentTask(workflow);
              const sortedSteps = [...workflow.workflowSteps].sort((a, b) => a.seqId - b.seqId);
              const currentStageIndex = getCurrentStageIndex(workflow);

              return (
                <Grid item xs={12} key={workflow.assignmentId}>
                  <Card>
                    <CardHeader
                      title={workflow.workflowName}
                      subheader={workflow.programName}
                      action={
                        (user?.role === 'ADMIN' || user?.role === 'AGENT' || user?.role === 'COMPANY') && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {workflow.userName}
                          </Typography>
                        )
                      }
                      sx={{ backgroundColor: '#f5f5f5' }}
                    />
                    <Divider />
                    <CardContent>
                      <Typography variant="h6" marginBottom="15px">Workflow Progress</Typography>
                      <Stepper alternativeLabel activeStep={currentStageIndex}>
                        {sortedSteps.map((step, index) => (
                          <Step key={step.id} completed={workflow.status === 'COMPLETED' || index < currentStageIndex}>
                            <StepLabel
                              StepIconComponent={(props) => (
                                workflow.status === 'COMPLETED' || index < currentStageIndex ? (
                                  <CheckCircleIcon color="success" />
                                ) : index === currentStageIndex ? (
                                  <RadioButtonUncheckedIcon color="primary" />
                                ) : (
                                  <RadioButtonUncheckedIcon color="disabled" />
                                )
                              )}
                            >
                              {step.stepName}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </CardContent>
                    <Divider />
                    {workflow.status !== 'COMPLETED' && (
                      <>
                        <CardContent>
                          <Typography variant="h6" marginBottom="15px">Current Task</Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={4}>
                              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Action
                              </Typography>
                              <Typography variant="body2">
                                {currentTask?.stepName || 'No task assigned'}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Instructions
                              </Typography>
                              <Typography variant="body2">
                                {currentTask?.instructions || 'No instructions available'}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Assigned To
                              </Typography>
                              <Typography variant="body2">
                                {currentTask?.stepActionOn || 'Not assigned'}
                              </Typography>
                            </Grid>
                          </Grid>
                          {/*{currentTask && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                              {getActionButtons(
                                user.role as UserType,
                                currentTask.stepActionOn as UserType,
                                currentTask.stepActionType as ActionType,
                                workflow.workflowStepTrackers.find(t => 
                                  t.stepId === currentTask.id && t.status === 'IN_PROGRESS'
                                )?.stepTrackerId || 0
                              )}
                            </Box>
                          )}*/}
                        </CardContent>
                        <Divider />
                      </>
                    )}
                    
                    {/* Workflow Instructions Section */}
                    {workflow.instructions && (
                      (workflow.showInstructionsAfterCompletion && workflow.status === 'COMPLETED') ||
                      (!workflow.showInstructionsAfterCompletion)
                    ) && (
                      <>
                        <Divider />
                        <CardContent>
                          <Typography variant="h6" marginBottom="15px">Workflow Instructions</Typography>
                          <Box
                            sx={{
                              maxHeight: '200px',
                              overflow: 'auto',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '12px',
                              backgroundColor: '#f9f9f9'
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {workflow.instructions}
                            </Typography>
                          </Box>
                        </CardContent>
                      </>
                    )}
                    
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        {workflow.status === 'COMPLETED' ? 'Completed On: ' : 'Last Updated: '}{workflow.updatedAt}
                      </Typography>
                      <Button 
                        variant="contained" 
                        onClick={() => handleViewDetails(workflow.assignmentId)}
                      >
                        View
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>
      <LoadingSpinner loading={loading} />
    </>
  );
};

export default Workflows; 